import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal, viewChild } from '@angular/core';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { FormControl, FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CompanyService } from '@infodocs/company/company.service';
import {
	ArchivedCompaniesTableComponent
} from '@infodocs/portfolio/modals/add-company-modal/archived-companies-table/archived-companies-table.component';
import {
	OnboardingContextFormComponent
} from '@infodocs/portfolio/modals/add-company-modal/onboarding-context-form/onboarding-context-form.component';
import {
	ManualAddCompanyComponent
} from '@infodocs/portfolio/modals/add-company-modal/manual-add-company/manual-add-company.component';
import { CompanyArchiveService } from '@infodocs/portfolio/services/company-archive.service';
import {
	NameReservationComponent
} from '@infodocs/portfolio/modals/add-company-modal/name-reservation/name-reservation.component';
import { CompanyPreviewComponent } from '@infodocs/portfolio/modals/company-preview/company-preview.component';
import SearchesForCompaniesComponent from '@infodocs/portfolio/modals/searches-for-companies.component';
import { CompanyArchive } from '@infodocs/portfolio/interfaces/company-archive';
import type { CompanyAddSearchLabel } from '@infodocs/portfolio/services/company-added-journey-logger.service';
import { ArchivedBadgeComponent } from '@infodocs/shared/components/archived-badge/archived-badge.component';
import { SearchButtonComponent } from '@infodocs/shared/components/buttons/search-button/search-button.component';
import { SubmitButtonComponent } from '@infodocs/shared/components/submit-button/submit-button.component';
import { ComponentLoaderComponent } from '@infodocs/shared/components/component-loader/component-loader.component';
import { EVENT_LOGGER_SERVICE } from '@infodocs/shared/modules/event-logging/token';
import { CompanyPortfolio } from '@infodocs/shared/interfaces/company-portfolio';
import { CompanyImportData } from '@infodocs/shared/interfaces/company-import-data';
import { CompanyExists, CompanySearchResult } from '@infodocs/shared/interfaces/company-search-result';
import { DirectorSearchResult } from '@infodocs/shared/interfaces/director-search-result';
import { IconComponent } from '@infodocs/shared/components/icon/icon.component';
import { SharedService } from '@infodocs/shared/shared.service';
import { luhnCheck } from '@infodocs/shared/forms/validators/custom-validators';
import {
	getCompanySearchErrorMessage,
	normalizeCompanySearchQuery
} from '@infodocs/shared/utils/company-search-errors';
import { resolveManualAddCompanyPrefill } from '@infodocs/shared/utils/manual-add-company-prefill';
import { AlertModule } from 'ngx-bootstrap/alert';
import { takeUntil } from 'rxjs';
import {
	faLandmark,
	faBoxArchive,
	faExclamationTriangle,
	faFolderOpen,
	faFolder,
} from '@fortawesome/free-solid-svg-icons';

export const DIRECTOR_SEARCH_ID_VALIDATION_MESSAGE =
	'Please enter a valid South African ID number.';

@Component({
	selector: 'app-add-company-modal',
	templateUrl: './add-company-modal.component.html',
	styleUrls: ['./add-company-modal.component.scss'],
	imports: [
		FormsModule,
		SearchButtonComponent,
		SubmitButtonComponent,
		IconComponent,
		AlertModule,
		RouterLink,
		ComponentLoaderComponent,
		ArchivedBadgeComponent,
		ArchivedCompaniesTableComponent,
		OnboardingContextFormComponent,
	]
})
export class AddCompanyModalComponent extends SearchesForCompaniesComponent implements OnInit {
	protected readonly faLandmarkIcon = faLandmark;
	protected readonly faBoxArchiveIcon = faBoxArchive;
	protected readonly faExclamationTriangleIcon = faExclamationTriangle;
	protected readonly faFolderOpenIcon = faFolderOpen;
	protected readonly faFolderIcon = faFolder;

	protected readonly onboardingForm = viewChild(OnboardingContextFormComponent);

	public state: FormControl<
		| 'welcome'
		| 'aboutYou'
		| 'search'
		| 'archives'
		| 'loading'
		| 'companyResults'
		| 'companyResultsError'
		| 'directorResults'
		| 'companyPreview'
		| 'directorPreview'
	> = new FormControl();

	public errors?: string[];
	protected readonly searchErrorMessage = signal<string | undefined>(undefined);
	protected readonly directorIdValidationError = signal<string | undefined>(undefined);
	// Plain properties: set via ngx-bootstrap modalService.show initialState (Object.assign overwrites signals).
	public searchOption: string = 'company';
	private readonly companySearchPlaceholder = 'Search by company name or registration number';
	private readonly directorSearchPlaceholder = 'Search by ID number (SA citizens only)';
	protected readonly searchPlaceholder = signal(this.companySearchPlaceholder);
	public searchQuery: string = '';
	public directorSearchId?: string;
	// Plain properties: restored via initialState when navigating back from company preview.
	public restoredModalState?: AddCompanyModalComponent['state']['value'];
	public restoredCompanyResults?: CompanySearchResult[];
	public restoredDirectorResults?: DirectorSearchResult[];

	public companyData: CompanyImportData = <CompanyImportData>{};
	protected readonly directorResults = signal<DirectorSearchResult[]>([]);
	protected readonly companyResults = signal<CompanySearchResult[]>([]);
	protected readonly sharedService = inject(SharedService);
	protected readonly penToSquareIcon = faPenToSquare;
	protected readonly accountHasCompanies = signal(false);
	protected readonly hasArchivedCompanies = signal(false);
	protected readonly openFolder = signal(false);
	protected readonly hoveredRowId = signal<string | null>(null);
	private readonly companyService = inject(CompanyService);
	private readonly archiveService = inject(CompanyArchiveService);
	private readonly eventLoggerService = inject(EVENT_LOGGER_SERVICE);

	protected get modalTitle(): string {
		const state = this.state.value;

		return state === 'welcome' || state === 'aboutYou' ? 'Welcome' : 'Add Company';
	}

	public ngOnInit() {
		const isRestoringModalState = this.isRestoringModalState();

		if (this.searchOption === 'director' || this.directorSearchId) {
			this.applySearchPlaceholder('director');
		}

		if (this.directorSearchId) {
			this.state.setValue('loading');
		} else if (isRestoringModalState) {
			this.applyRestoredSearchState();
		}

		this.state.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(state => {
				const eventData = {
					event: 'Form State Changed',
					form: 'addCompanyForm',
					state: state
				};
				this.eventLoggerService.logEvent(eventData.event, eventData);
			});

		this.companyService.getPortfolioCompanies()
			.pipe(takeUntil(this.destroy$))
			.subscribe((res: CompanyPortfolio) => {
				this.accountHasCompanies.set(res.data.length > 0);

				if (!this.directorSearchId && !isRestoringModalState) {
					this.state.setValue(res.data.length === 0 ? 'welcome' : 'search');
				}
			});

		this.archiveService.listArchives()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: archives => {
					this.hasArchivedCompanies.set(archives.length > 0);
				},
				error: () => {
					this.hasArchivedCompanies.set(false);
				}
			});

		// auto search if query pre-populated
		if (this.directorSearchId) {
			this.searchDirectorByIdWithDisplayName(this.directorSearchId, this.searchQuery);
		} else if (!!this.searchQuery && !isRestoringModalState) {
			this.search(this.searchOption, this.searchQuery);
		}
	}

	protected continueFromWelcome(): void {
		this.state.setValue('aboutYou');
	}

	protected onOnboardingFinished(): void {
		this.state.setValue('search');
	}

	// reset search queries, options, placeholders and results
	resetSearch(option: string) {
		this.searchQuery = '';
		this.applySearchPlaceholder(option);
		this.clearSearchResults();
		this.state.setValue('search');
	}

	protected onSearchQueryChange(query: string): void {
		this.directorIdValidationError.set(undefined);

		if (query !== '') {
			return;
		}

		this.applySearchPlaceholder(this.searchOption);
		if (this.searchOption !== 'director') {
			return;
		}

		this.clearSearchResults();
		this.state.setValue('search');
	}

	protected override previewCompany(company: CompanySearchResult) {
		const restoreState = this.buildSearchRestoreState();

		this.modalRef.hide();

		return this.modalService.show(CompanyPreviewComponent, {
			initialState: {
				registrationNumber: company.CompanyInformation.CompanyRegistrationNumber,
				companyExists: company.CompanyExists ?? company.CompanyInformation.CompanyExists,
				existingCompanyId: company.CompanyInformation.CompanyId,
				companySearchResult: company,
				search_option: this.getSearchOption(),
				onBack: () => this.reopenWithSearchState(restoreState),
				onFlowComplete: () => this.onFlowComplete(),
			}
		});
	}

	protected previewArchive(archive: CompanyArchive): void {
		const companyExists: CompanyExists = archive.linked_to_another_account
			? 'Request access'
			: 'Archived';
		const company = this.buildArchiveSearchResult(archive, companyExists);
		const restoreState: Partial<AddCompanyModalComponent> = {
			restoredModalState: 'archives',
			onFlowComplete: this.onFlowComplete,
		};

		this.modalRef.hide();

		this.modalService.show(CompanyPreviewComponent, {
			initialState: {
				registrationNumber: archive.registration_number ?? undefined,
				companyExists,
				existingCompanyId: String(archive.company_id),
				companySearchResult: company,
				search_option: this.getSearchOption(),
				onBack: () => this.reopenWithSearchState(restoreState),
				onFlowComplete: () => this.onFlowComplete(),
			}
		});
	}

	protected override onBack() {
		return this.reopenWithSearchState(this.buildSearchRestoreState());
	}

	private reopenWithSearchState(restoreState: Partial<AddCompanyModalComponent>) {
		return this.modalService.show(AddCompanyModalComponent, {
			ignoreBackdropClick: true,
			initialState: restoreState,
		});
	}

	private buildSearchRestoreState(): Partial<AddCompanyModalComponent> {
		return {
			searchQuery: this.searchQuery,
			searchOption: this.searchOption,
			restoredModalState: this.state.value ?? undefined,
			restoredCompanyResults: this.companyResults(),
			restoredDirectorResults: this.directorResults(),
			onFlowComplete: this.onFlowComplete,
		};
	}

	private isRestoringModalState(): boolean {
		const restorableStates = [
			'companyResults',
			'companyResultsError',
			'directorResults',
			'directorPreview',
			'archives',
		];
		return !!this.restoredModalState && restorableStates.includes(this.restoredModalState);
	}

	private applyRestoredSearchState(): void {
		if (this.restoredCompanyResults) {
			this.companyResults.set(this.restoredCompanyResults);
		}

		if (this.restoredDirectorResults) {
			this.directorResults.set(this.restoredDirectorResults);
		}

		this.applySearchPlaceholder(this.searchOption);

		if (this.restoredModalState) {
			this.state.setValue(this.restoredModalState);
		}
	}

	// fetch list of companies/directors by company name or registration/ID number
	search(option: string, query: string) {
		const trimmedQuery = (query ?? '').trim();
		if (!trimmedQuery) {
			this.state.setValue('loading');
			return;
		}

		this.searchErrorMessage.set(undefined);
		this.directorIdValidationError.set(undefined);

		if (option === 'director' && !luhnCheck(trimmedQuery)) {
			this.directorIdValidationError.set(DIRECTOR_SEARCH_ID_VALIDATION_MESSAGE);
			this.state.setValue('search');
			return;
		}

		this.state.setValue('loading');

		// company search
		if (option === 'company') {
			this.accountService.searchCompany(normalizeCompanySearchQuery(trimmedQuery))
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: result => {
						this.companyResults.set(result); // TODO: [backend] remove the "CompanyInformation" key on the backend
						this.state.setValue('companyResults');
					},

					error: (err: HttpErrorResponse) => {
						this.searchErrorMessage.set(getCompanySearchErrorMessage(err, this.searchQuery));
						this.state.setValue('search');
					}
				});
		}

		// director search
		else if (option === 'director') {
			this.accountService.searchDirector(trimmedQuery).subscribe({
				next: (result: any) => {
					result = result.length === 0 ? [] : [result]; // TODO: [backend] always return an array so we can skip this line
					this.directorResults.set(result);
					this.state.setValue('directorResults');
				},
				error: (err: any) => {
					// TODO: [after rebuild] display errors
					// this.errors = err.error
					console.log('Looks like something went wrong...');
					this.state.setValue('search');
				}
			});
		}
	}

	private searchDirectorByIdWithDisplayName(idNumber: string, displayName: string) {
		this.state.setValue('loading');
		this.searchOption = 'director';
		this.searchQuery = displayName;
		this.applySearchPlaceholder('director');

		this.accountService
			.searchDirector(idNumber)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result: DirectorSearchResult | DirectorSearchResult[]) => {
					const director = Array.isArray(result) ? result[0] : result;
					if (director?.Directorships) {
						this.directorResults.set([director]);
						this.companyResults.set(director.Directorships);
						this.state.setValue('directorPreview');
					} else {
						this.directorResults.set([]);
						this.state.setValue('directorResults');
					}
					this.directorSearchId = undefined;
				},
				error: () => {
					this.state.setValue('search');
					this.directorSearchId = undefined;
				}
			});
	}

	// display a list of the director's companies
	previewDirectorCompanies(director: DirectorSearchResult) {
		this.state.setValue('loading');
		this.searchOption = 'director';
		this.applySearchPlaceholder('director');

		// update search query to name
		if (director.DirectorInformation.IdNumber !== this.directorResults()[0].DirectorInformation.IdNumber) {
			this.search('director', director.DirectorInformation.IdNumber);
		} else {
			// display director name in search input
			this.searchQuery = director.DirectorInformation.FirstNames + ' ' + director.DirectorInformation.Surname;
		}

		this.state.setValue('directorPreview');
		this.companyResults.set(director.Directorships);
	}

	protected openManualAddCompanyModal() {
		this.modalRef?.hide();

		const searchHits = this.searchOption === 'company' ? this.companyResults() : [];

		this.modalService.show(ManualAddCompanyComponent, {
			class: 'modal-dialog',
			ignoreBackdropClick: true,
			initialState: resolveManualAddCompanyPrefill(this.searchQuery, searchHits)
		});
	}

	protected openAddNewCompanyModal() {
		this.modalRef?.hide();

		return this.modalService.show(NameReservationComponent, {
			class: 'modal-dialog modal-xl',
			ignoreBackdropClick: true
		});
	}

	protected goToArchives() {
		this.state.setValue('archives');
	}

	protected goBackFromArchives() {
		this.state.setValue('search');
	}

	protected override getSearchOption(): CompanyAddSearchLabel {
		return this.searchOption === 'company' ? 'Company Search' : 'Director Search';
	}

	private applySearchPlaceholder(option: string): void {
		this.searchPlaceholder.set(
			option === 'director' ? this.directorSearchPlaceholder : this.companySearchPlaceholder
		);
	}

	private clearSearchResults(): void {
		this.directorSearchId = undefined;
		this.searchErrorMessage.set(undefined);
		this.directorIdValidationError.set(undefined);
		this.companyResults.set([]);
		this.directorResults.set([]);
		this.companyData = <CompanyImportData>{};
	}

	protected toggleFolderOpen(open: boolean, rowId: string | null): void {
		this.hoveredRowId.set(rowId);
		this.openFolder.set(open);
	}

	private buildArchiveSearchResult(
		archive: CompanyArchive,
		companyExists: CompanyExists
	): CompanySearchResult {
		return {
			CompanyExists: companyExists,
			CompanyInformation: {
				CompanyId: String(archive.company_id),
				CompanyName: archive.company_name ?? '',
				CompanyRegistrationNumber: archive.registration_number ?? '',
				CompanyExists: companyExists,
			},
		};
	}
}
